import _ from "lodash";
import React, { useState, useEffect } from "react";
import UIModal from "UIComponents/UIModal";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import LEPreview from "AppComponents/LEPreview";
import LeFeed from "AppComponents/LeFeed";
import TopHeader from "AppComponents/TopHeaderNavBar";
import WorksheetPreview from "Worksheet/components/WorksheetPreview";
import classes from "./LeSearchModal.scss";
import {
  importAssessmentV2,
  cloneAssessment,
} from "Assessments/modules/AssessmentModule";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { getUnitPlanDetailsFromCache } from "IBPlanner/modules/IBGraphqlHelpers";
import { getUnitPlanFieldResponseFilter } from "UnitPlans/modules/Utils";
import { getUnitPlanCentralRepoInputAndFilters } from "Courses/modules/utils";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getGradeBasicDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  CURRICULUM_TYPE_PYP,
  COMMUNITY_ORG_ID,
} from "Constants/stringConstants";
import { Button, EmptyState } from "@toddle-edu/ds-web";
import ACLStore from "lib/aclStore";
import { ENABLE_ASSESSMENTS_IN_PYP } from "Constants/permissionConstants";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import {
  setGlobalTrayEnabledState,
  setGlobalHeaderVisibility,
} from "Login/modules/LoginModule";
import { hideIntercom, showIntercom } from "modules/Services";
import { LEIllustraton } from "@toddle-edu/ds-theme";
import { colors } from "Constants";
import { getUserName } from "Login/routes/ToddleLibrary/modules/ToddleLibraryUtils";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";

const styles = {
  leFeedStyles: {
    feedInnerContainerStyle: {
      width: "100%",
    },
    headerContainerStyle: {
      padding: "40px 56px 0",
      width: "100%",
    },
    feedContainerStyle: {
      alignItems: "center",
      padding: "0 56px",
    },
    customNonSelectedContainerStyle: {
      borderRadius: "8px",
    },
    customSelectedContainerStyle: {
      borderRadius: "8px",
    },
    feedDataListContainerStyle: {
      height: "80px",
    },
    imageContainerStyle: {
      width: "48px",
      height: "48px",
      borderRadius: "8px",
    },
    feedItemTitleStyle: {
      color: colors.textDefault,
      fontFamily: "Avenir Next",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "24px",
    },
  },
  topHeaderProps: {
    backButtonSize: "medium",
    toddleIconHeight: 40,
    toddleIconWidth: 40,
    containerStyle: {
      padding: "12px 48px 12px 16px",
    },
  },
};

const LeSearchModal = ({
  id,
  onLeSearchModalClose,
  t,
  importAssessmentV2,
  portalType,
  unitPlanId,
  onSuccess,
  eventTarget,
  assessmentTypes,
  curriculumType,
  importFromLeLibrary,
  importFromToddleLibrary,
  groupType,
  organizationId,
  selectedCourseId,
  academicYearId,
  setGlobalTrayEnabledState,
  hideIntercom,
  showIntercom,
  cloneAssessment,
  activeLibraryEntityType,
  setActiveLibraryEntityType,
  endpoint,
  destination = null,
  courseAcademicYearsIds,
  globalGradeUid,
  isGlobalHeaderVisible,
  setGlobalHeaderVisibility,
  testId = "communityCommonComponents-LeSearchModal",
  customLeFeedStyles = {},
  shouldFetchUnitPlanIdFromState = true,
  learningCourseId,
  createEntityFlowResource = false,
  shouldIncludeQuickTasks = false,
  isMultiSelect = false,
}) => {
  const [assessmentId, setAssessmentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [worksheetItem, setWorksheetItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  const isImportWorksheet = groupType === "WORKSHEET";

  const updateSelectedItems = items => {
    setSelectedItems(items);
  };

  const onAddClick = async () => {
    setLoading(true);

    /** We need to clone assessment when importing to class stream */
    if (_.isEqual(endpoint, "CLASS_STREAM")) {
      const { clonedAssessmentId } = await cloneAssessment({
        sourceAssessmentId: assessmentId,
        academicYears: courseAcademicYearsIds,
        sourceOrganizationId: COMMUNITY_ORG_ID,
        learningCourseId,
      });

      setLoading(false);
      if (clonedAssessmentId) {
        onLeSearchModalClose({ assessmentId, clonedAssessmentId });
        if (onSuccess) {
          onSuccess(clonedAssessmentId);
        }
      }
      return;
    }

    const { centralRepoInput } = getUnitPlanCentralRepoInputAndFilters({
      organizationId: organizationId,
      courseId: selectedCourseId,
      academicYearId,
    });

    const responseFilter = getUnitPlanFieldResponseFilter();
    const collaboratorsFilter = {
      userAcademicYearId: academicYearId,
    };

    const unitPlanDetails = getUnitPlanDetailsFromCache({
      unitPlanId,
      centralRepoInput,
      responseFilter,
      collaboratorsFilter,
      exceptionalCollaboratorsFilter: collaboratorsFilter,
    });

    const unitPlanAcademicYearsIds = _.map(
      _.get(unitPlanDetails, "academicYears", []),
      item => item.id
    );

    const importIds = await importAssessmentV2({
      sourceAssessmentIds: isMultiSelect ? selectedItems : [assessmentId],
      unitId: unitPlanId,
      eventTarget: eventTarget || "learning experience search modal",
      assessmentTypes,
      academicYears: unitPlanAcademicYearsIds,
      userInfoPortalType: portalType,
      shouldFetchUnitPlanIdFromState,
      destination,
      createEntityFlowResource,
    });

    setLoading(false);
    if (importIds) {
      onLeSearchModalClose();
      if (onSuccess) {
        onSuccess(importIds);
      }
    }
  };

  useEffect(() => {
    setGlobalTrayEnabledState(false); // For Hiding the tray on Component Mounted
    hideIntercom();
    return () => {
      setGlobalTrayEnabledState(true); // For showing the tray on Component Unmounted.
      showIntercom();
    };
  }, []);

  const getHeaderTitle = () => {
    let headerTitle = t("community:search_on_toddle_community");

    if (importFromLeLibrary) {
      if (isImportWorksheet) {
        headerTitle = t("worksheet:add_from_worksheet_library");
      } else {
        const isPYP = _.isEqual(curriculumType, CURRICULUM_TYPE_PYP);

        headerTitle = isPYP
          ? t("common:add_from_learning_experience_library")
          : t("common:add_from_learning_experience_&_assessment_library");
      }
    } else if (importFromToddleLibrary) {
      headerTitle = t("toddleLibrary:import_from_toddle_library");
    } else if (isImportWorksheet) {
      headerTitle = t("common:import_from_worksheet_library");
    }

    return headerTitle;
  };

  const getSubTitle = () => {
    return t("common:import_from_le_modal_subtext", {
      label: isImportWorksheet
        ? t("common:worksheet_lowercase")
        : _.isEqual(curriculumType, CURRICULUM_TYPE_PYP)
        ? _.capitalize(t("common:le_label"))
        : t("common:learning_experience_or_assessment"),
    });
  };

  const isPYPAssessmentDisabled =
    !ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP) && curriculumType === "IB_PYP";

  let emptyText;
  if (isImportWorksheet) {
    emptyText = t("common:select_worksheet_to_preview");
  } else if (isPYPAssessmentDisabled || importFromToddleLibrary) {
    emptyText =
      activeLibraryEntityType == "worksheet"
        ? t("common:select_worksheet_to_preview")
        : t("common:select_to_preview");
  } else {
    emptyText = t("common:select_post_to_preview");
  }

  const toddleLibraryProps = {
    displayCountHeadingWithFilters: true,
    tabDropdownVariant: "outlined-subtle",
    ...styles.leFeedStyles,
  };

  const imageUrl = _.get(worksheetItem, "imageUrl", "");

  // toddle library already has count heading next to filters
  const displayCountHeadingWithSearch = importFromToddleLibrary
    ? false
    : customLeFeedStyles?.displayCountHeadingWithSearch;

  const LeFeedComponent = (
    <LeFeed
      importFromLeLibrary={importFromLeLibrary}
      importFromToddleLibrary={importFromToddleLibrary}
      activeEntityType={activeLibraryEntityType}
      setActiveEntityType={setActiveLibraryEntityType}
      setWorksheetItem={setWorksheetItem}
      showSubText={false}
      showHeaderText={false}
      shouldIncludeQuickTasks={shouldIncludeQuickTasks}
      assessmentId={assessmentId}
      setAssessmentId={setAssessmentId}
      portalType={portalType}
      curriculumType={curriculumType}
      groupType={groupType}
      globalGradeUid={globalGradeUid}
      endpoint={endpoint}
      testId={testId}
      isMultiSelect={isMultiSelect}
      selectedItems={selectedItems}
      updateSelectedItems={updateSelectedItems}
      {...(importFromToddleLibrary ? toddleLibraryProps : {})}
      {...customLeFeedStyles}
      displayCountHeadingWithSearch={displayCountHeadingWithSearch}
    />
  );

  const getClassStreamHeader = () => {
    return (
      <TopHeader
        headerText={<span className={"heading-5"}>{getHeaderTitle()}</span>}
        onBack={onLeSearchModalClose}
        testId={testId}
        {...styles.topHeaderProps}
      />
    );
  };

  return (
    <UIModal
      isOpen={true}
      onRequestClose={onLeSearchModalClose}
      isFullScreen={true}
      showFromTopOfTheViewPort={true}
    >
      <div className={classes.container}>
        <ConditionalWrapper
          condition={_.isEqual(endpoint, "CLASS_STREAM")}
          wrapper={getClassStreamHeader}
        >
          <TopHeader
            headerText={<span className={"heading-4"}>{getHeaderTitle()}</span>}
            subHeaderText={getSubTitle()}
            onBack={onLeSearchModalClose}
            testId={testId}
          />
        </ConditionalWrapper>
        <div className={classes.mainContent}>
          <div className={classes.leftPane}>
            {LeFeedComponent}
            <div className={classes.footer}>
              <Button
                onClick={onLeSearchModalClose}
                _legacy_variant={"dismissive"}
                variant="neutral"
                type="outlined"
                testId={`${testId}-cancel`}
              >
                {t("common:cancel")}
              </Button>
              <Button
                onClick={onAddClick}
                _legacy_variant={"progressive"}
                variant="progressive"
                type="fill"
                disabled={
                  !isMultiSelect ? !assessmentId : selectedItems.length === 0
                }
                size={"medium"}
                testId={`${testId}-next`}
              >
                {t(importFromToddleLibrary ? "common:next" : "common:add")}
              </Button>
            </div>
          </div>
          <div className={classes.rightPane}>
            {assessmentId ? (
              activeLibraryEntityType == "worksheet" || isImportWorksheet ? (
                <WorksheetPreview
                  t={t}
                  worksheetItem={worksheetItem}
                  assessmentId={assessmentId}
                  id={id}
                  imageUrl={imageUrl}
                  isImportWorksheet={isImportWorksheet}
                  importFromToddleLibrary={importFromToddleLibrary}
                  portalType={
                    activeLibraryEntityType === "worksheet" ? "COMMUNITY" : null
                  }
                />
              ) : (
                <LEPreview
                  assessmentId={assessmentId}
                  portalType={portalType}
                  parentType={"UNIT_PLAN"}
                  attachmentItemHeight={100}
                  showNewHeader={importFromToddleLibrary}
                />
              )
            ) : (
              <EmptyState subtitle={emptyText} illustration={LEIllustraton} />
            )}
          </div>
        </div>
      </div>
      {!!loading && <FullScreenLoader />}
    </UIModal>
  );
};

const mapStateToProps = state => {
  const id = _.get(state, "login.userInfo.id", "");
  const organizationId = _.get(state, "login.userInfo.org_id", "");
  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    ""
  );
  const academicYearId = getSelectedAcademicYearId(state);

  const userName = getUserName(state);

  const courseId = state.teacher.selected_class.selected_course;
  const courseGradeFilters = {
    academicYearId,
  };
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });
  const courseAcademicYearsIds = _.map(
    _.get(courseDetails, "academicYears", []),
    item => item.id
  );

  const gradeId = _.get(state, "teacher.selected_class.selected_grades[0]", "");
  let globalGradeUid;
  if (gradeId) {
    const gradeDetails = getGradeBasicDetailsFromCache(gradeId);
    globalGradeUid = _.get(gradeDetails, "globalGrade.uid", null);
  }

  const isGlobalHeaderVisible = _.get(
    state,
    "login.globalHeader.isVisible",
    false
  );

  return {
    id,
    userName,
    curriculumType: _.get(state, "platform.currentCurriculumProgram.type", ""),
    organizationId,
    selectedCourseId,
    academicYearId,
    courseAcademicYearsIds,
    globalGradeUid,
    isGlobalHeaderVisible,
    learningCourseId: getSelectedLearningCourseId(state),
  };
};

const mapActionCreators = {
  importAssessmentV2,
  cloneAssessment,
  setGlobalTrayEnabledState,
  hideIntercom,
  showIntercom,
  setGlobalHeaderVisibility,
};

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators)
)(LeSearchModal);
